import { type SVGProps, type ChangeEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import useValidatedTokenAmount from '@/shared/hooks/useValidatedTokenAmount';
import useValidatedTokenAmountInput from '@/shared/hooks/useValidatedTokenAmountInput';
import Chevron from '@/shared/icons/flip-ui-kit/large/Chevron';
import { formatUsdValue } from '@/shared/utils/numbers';
import type TokenAmount from '@/shared/utils/TokenAmount';

const UnknownTokenLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="33"
    height="34"
    viewBox="0 0 33 34"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <g filter="url(#filter0_i_2103_18586)">
      <rect y="0.382782" width="32" height="32" rx="16" fill="#2C2D2C" />
      <rect
        y="0.382782"
        width="32"
        height="32"
        rx="16"
        fill="url(#paint0_radial_2103_18586)"
        fillOpacity="0.15"
      />
    </g>
    <g opacity="0.2" filter="url(#filter1_f_2103_18586)">
      <circle cx="16.0344" cy="17.3606" r="6.60241" fill="#D9D9D9" />
    </g>
    <path
      d="M13.0634 13.7293C13.0634 13.7293 13.3935 10.7582 16.0344 10.7582C18.6754 10.7582 19.0055 12.4088 19.0055 13.7293C19.0055 14.7213 18.4464 15.7136 17.3282 16.1456C16.6481 16.4084 16.0344 16.9614 16.0344 17.6907V18.0208"
      stroke="url(#paint1_radial_2103_18586)"
      strokeWidth="1.98072"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.6946 21.6523C16.6946 22.0168 16.399 22.3125 16.0344 22.3125C15.6698 22.3125 15.3741 22.0168 15.3741 21.6523C15.3741 21.2877 15.6698 20.992 16.0344 20.992C16.399 20.992 16.6946 21.2877 16.6946 21.6523Z"
      stroke="url(#paint2_radial_2103_18586)"
      strokeWidth="1.32048"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <filter
        id="filter0_i_2103_18586"
        x="0"
        y="0.382782"
        width="33.9002"
        height="33.9002"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dx="1.90017" dy="1.90017" />
        <feGaussianBlur stdDeviation="6.17555" />
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
        <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2103_18586" />
      </filter>
      <filter
        id="filter1_f_2103_18586"
        x="-6.86646e-05"
        y="1.32617"
        width="32.0689"
        height="32.0688"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="4.71601" result="effect1_foregroundBlur_2103_18586" />
      </filter>
      <radialGradient
        id="paint0_radial_2103_18586"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(16 0.382782) rotate(90) scale(20.4622)"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_2103_18586"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(15.5629 11.7014) rotate(70.71) scale(9.99304 8.17612)"
      >
        <stop stopColor="#CDCDCD" />
        <stop offset="1" stopColor="#CDCDCD" stopOpacity="0" />
      </radialGradient>
      <radialGradient
        id="paint2_radial_2103_18586"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(11.3183 19.7186) rotate(32.0054) scale(8.89814)"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);

const BalanceDisplay = ({
  balance,
  token,
  label,
  onClick,
}: {
  balance: TokenAmount;
  token: ChainflipToken;
  label: string;
  onClick: (amount: BigNumber.Value, capped?: boolean) => void;
}) => (
  <div className="text-right text-12 text-cf-light-2">
    {label}:{' '}
    <button
      type="button"
      onClick={() => onClick(balance.toFixed(), false)}
      className="whitespace-nowrap font-aeonikMedium text-cf-white"
    >
      {balance.toFormatted()} {token.symbol}
    </button>
  </div>
);

export interface TokenInputFieldProps {
  token: ChainflipToken | undefined;
  value: TokenAmount | undefined;
  onChange: (value: TokenAmount | undefined) => void;
  balance?: TokenAmount;
  accountBalance?: TokenAmount;
  maxAmount?: TokenAmount;
  subtext?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  displayChain?: boolean;
  focusOnMount?: boolean;
  onTokenClick?: () => void;
  tokenWidth?: number;
  acceptOverflowAmount?: boolean;
  setHasError?: (value: boolean) => void;
  onClickMax?: VoidFunction;
  noBalanceLabel?: boolean;
  noError?: boolean;
  hideTokenLabel?: boolean;
  allowZero?: boolean;
  maxAmountErrorOverride?: string;
}

export default function TokenInputField({
  token,
  value,
  onChange,
  balance,
  accountBalance,
  maxAmount,
  subtext,
  label,
  error,
  disabled = false,
  displayChain = true,
  focusOnMount = false,
  onTokenClick,
  tokenWidth,
  acceptOverflowAmount = false,
  setHasError,
  onClickMax,
  noBalanceLabel,
  noError,
  hideTokenLabel,
  allowZero,
  maxAmountErrorOverride,
}: TokenInputFieldProps): React.JSX.Element {
  const { price } = useTokenPrice(token);
  const effectiveMaxAmount = maxAmount ?? balance;
  const [maxAmountError, setMaxAmountError] = useState<string>();
  const displayedError = maxAmountError ?? error;

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    onChange: onInputChange,
    value: inputValue,
    setValue: setInputValue,
  } = useValidatedTokenAmountInput({
    token,
    initialValue: value?.toFixed() ?? '',
  });

  const validatedAmount = useValidatedTokenAmount(inputValue, token);
  const usdAmount = validatedAmount?.mul(price ?? 0).toNumber() ?? 0;
  const displayedSubtext = subtext ?? formatUsdValue(usdAmount);

  useEffect(() => {
    if (effectiveMaxAmount && validatedAmount && validatedAmount.gt(effectiveMaxAmount)) {
      if (acceptOverflowAmount) {
        onChange(validatedAmount);
      } else {
        onChange(undefined);
      }
      setMaxAmountError(
        maxAmountErrorOverride ?? `Amount exceeds ${maxAmount ? 'maximum' : 'balance'}`,
      );
      setHasError?.(true);
    } else {
      onChange(validatedAmount);
      setMaxAmountError(undefined);
      setHasError?.(false);
    }
  }, [
    validatedAmount,
    effectiveMaxAmount,
    maxAmount,
    maxAmountErrorOverride,
    onChange,
    setHasError,
    acceptOverflowAmount,
  ]);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, []);

  return (
    <div className="w-full">
      <div
        className={classNames(
          'relative flex h-[4.5rem] flex-row text-cf-light-4',
          disabled && 'cursor-not-allowed [&>*]:pointer-events-none',
        )}
      >
        <div
          className={classNames(
            'relative w-0 grow rounded-l-md border border-cf-gray-4 bg-cf-gray-3-5 shadow-grayPop3 transition',
            displayedError && '!border-cf-red-1',
            !disabled && 'group transition hover:border-cf-gray-5 hover:bg-cf-gray-4', // apply group-hover effect only when enabled
          )}
        >
          <div className="absolute left-2 top-[-10px] z-10 select-none px-[6px] text-12 text-cf-light-2">
            <div>{label}</div>
            <div className="absolute inset-x-0 bottom-0 top-[9px] -z-10 bg-cf-gray-3-5 transition group-hover:bg-cf-gray-4" />
          </div>
          <button
            className={classNames(
              'absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-cf-gray-5 bg-cf-gray-4 px-1.5 py-0.5 text-10 opacity-0 transition duration-100 ease-out',
              'z-10 hover:border-cf-light-1 hover:bg-cf-gray-5 disabled:pointer-events-none enabled:group-focus-within:opacity-100 enabled:group-hover:opacity-100',
            )}
            onClick={() => {
              setInputValue(effectiveMaxAmount!.toFixed());
              onClickMax?.();
            }}
            type="button"
            disabled={
              disabled ||
              !effectiveMaxAmount ||
              !effectiveMaxAmount.gt(0) ||
              effectiveMaxAmount.eq(validatedAmount)
            }
          >
            MAX
          </button>
          <input
            placeholder="0"
            type="text"
            inputMode="decimal"
            className={classNames(
              'peer h-full w-full bg-transparent pl-3 pr-2 font-aeonikMedium text-20 text-white outline-none transition placeholder:text-cf-light-2',
              displayedSubtext && '-translate-y-2',
            )}
            value={inputValue}
            onChange={onInputChange}
            onFocus={() =>
              // Set the value to the raw string value (unformatted/without commas) when the input is focused
              onInputChange({
                target: { value: inputValue },
              } as ChangeEvent<HTMLInputElement>)
            }
            onBlur={() => {
              if (validatedAmount) {
                setInputValue(!allowZero && validatedAmount.eq(0) ? '' : validatedAmount.toFixed());
              }
            }}
            disabled={disabled}
            ref={inputRef}
          />
          <span
            className={classNames(
              'absolute bottom-0 left-0 max-w-full truncate pb-2.5 pl-3 text-12 text-cf-light-3/50 transition',
              !displayedSubtext && 'opacity-0',
            )}
          >
            {displayedSubtext}
          </span>
        </div>
        <button
          className={classNames(
            'flex items-center gap-3 rounded-r-md border border-cf-gray-4 bg-cf-gray-3-5 px-4 shadow-grayPop3',
            !disabled && onTokenClick && 'hover:border-cf-gray-5 hover:bg-cf-gray-4',
          )}
          style={{ width: tokenWidth }}
          disabled={disabled || !onTokenClick}
          onClick={onTokenClick}
          type="button"
        >
          {token ? (
            <TokenWithChainByAsset size="medium" asset={token.chainflipId} />
          ) : (
            <UnknownTokenLogo width={24} height={24} />
          )}
          {!hideTokenLabel &&
            (token ? (
              <div className="flex min-w-0 grow flex-col items-start">
                <div className="font-aeonikBold text-14 text-white">{token.symbol}</div>
                {displayChain && (
                  <span className="-mt-0.5 w-full truncate text-left text-12 text-cf-light-2">
                    on {token.chain.name}
                  </span>
                )}
              </div>
            ) : (
              <div className="font-aeonikBold text-14 text-white">Select</div>
            ))}
          {onTokenClick && <Chevron orientation="left-right" className="-mx-2" />}
        </button>
      </div>
      <div className="flex flex-row justify-between pt-2">
        {!noError && (
          <div className="text-12 text-cf-red-1">
            {displayedError}
            &nbsp;{/* prevent layout jump when error appears */}
          </div>
        )}
        <div className="flex flex-col items-end gap-y-1">
          {token && balance && !noBalanceLabel && (
            <BalanceDisplay
              balance={balance}
              token={token}
              label={accountBalance ? 'Connected Wallet Balance' : 'Balance'}
              onClick={setInputValue}
            />
          )}
          {token && accountBalance && !noBalanceLabel && (
            <BalanceDisplay
              balance={accountBalance}
              token={token}
              label="Account Balance"
              onClick={setInputValue}
            />
          )}
        </div>
      </div>
    </div>
  );
}
